import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { AuthUser } from '@app/account/account-api.model';
import { TargetResourceModalService } from '@app/flex-framework/components/modals/services/target-resource-modal.service';
import {
  FlexRowSummary,
  TargetResource,
} from '@app/flex-framework/flex-api.model';
import { OrgEndorsedService } from '@app/orgs/services/org-endorsed.service';
import { Key } from '@app/shared/key';
import { AuthService } from '@app/shared/services/auth.service';
import { TargetsService } from '@app/shared/services/targets.service';
import { TargetService } from '@app/target/services/target.service';
import {
  ResourceReference,
  ResourceResult,
  Target,
} from '@app/target/target-api.model';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { fromEvent, Observable, of, Subscription } from 'rxjs';
import { catchError, debounceTime, filter, switchMap } from 'rxjs/operators';
import { NgbDropdown } from '@ng-bootstrap/ng-bootstrap';
import { LDFlagsService } from '@app/shared/services/ld-flags.service';

@Component({
  selector: 'dgx-target-resources-modal',
  templateUrl: './target-resources-modal.component.html',
  styleUrls: ['./target-resources-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
/** @deprecated This is now *only* used on opportunities to add Targets OR Pathways. Targets itself uses {@link add-by-search-base.component.ts }
 * The component should be refactored to fit opportunities only instead.
 */
export class TargetResourcesModal implements OnInit, OnDestroy, AfterViewInit {
  @Input() public target: Target;
  @Input() public resourceSection: FlexRowSummary;
  @Input() public existingResources: TargetResource[];
  @Input() public customSaveResources;

  @ViewChild('searchInput') public inputElRef: ElementRef;
  @ViewChild('ngbDropdown') public ngbDropdownRef: NgbDropdown;

  public i18n = this.translate.instant([
    'TargetResourcesForm_InputHelp',
    'TargetResourcesForm_Suggestions',
    'TargetResourcesForm_Selected',
    'Core_Remove',
    'Core_Saving',
    'TargetResourcesForm_NoResults',
    'TargetResourcesForm_NoResultsFound',
    'TargetResourcesForm_Title',
    'TargetResourcesForm_SearchPlaceHolder',
  ]);

  public newItem: ResourceResult | string;
  public term: string;
  public selectedSearchResults: ResourceResult[] = [];
  public suggestions: ResourceResult[] = [];
  public initialized: boolean = false;
  public showSuggestions: boolean = false;
  public noResults: boolean = false;
  public saving: boolean = false;

  private hasOrgManagedContent = false;
  private inputKeyUp$: Subscription;
  private authUser: AuthUser;
  private orgManagedNotificationFlag = this.ldFlagsService.orgManagedLDFlag;
  private disableLearnerSkillsRegistry: boolean = true;

  constructor(
    public activeModal: NgbActiveModal,
    private authService: AuthService,
    private ldFlagsService: LDFlagsService,
    private orgEndorsedService: OrgEndorsedService,
    private targetResourceModalService: TargetResourceModalService,
    private targetService: TargetService,
    private targetsService: TargetsService,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef,
    private ngzone: NgZone
  ) {}

  public get showOrgManagedNotification() {
    return (
      this.orgManagedNotificationFlag &&
      this.hasOrgManagedContent &&
      !this.target?.organizationId
    );
  }

  public get showCreatePlanButton(): boolean {
    return (
      this.authUser?.canManageTargets &&
      this.resourceSection.resourceType === 'Target'
    );
  }

  public get endorsedSrc(): string {
    return this.resourceSection.resourceType === 'Tag'
      ? this.orgEndorsedService.getEndorsedSrc(this.target.organizationId)
      : '';
  }

  public get allowCustomResources(): boolean {
    return this.resourceSection.resourceType === 'Tag';
  }

  public get createAPlanLabel(): string {
    const compositeResourceKey =
      this.targetsService.composeTargetResourceStringKey({
        baseKey: 'TargetResources_',
        primaryResourceType: this.resourceSection.resourceType,
        orSecondary: true,
      });
    return this.translate.instant(`TargetResources_CreateAResourceFormat`, {
      targetResourceType: this.translate.instant(compositeResourceKey),
    });
  }

  public get noItemsSelected(): string {
    return this.translate.instant(
      `TargetResources_No${this.resourceSection.resourceType}sSelectedYet`
    );
  }

  public get showSuggestionsHeader(): boolean {
    return (
      this.resourceSection.resourceType !== 'Tag' &&
      this.resourceSection.resourceType !== 'User'
    );
  }

  public get isSkillRegistryEnabled(): boolean {
    return !this.disableLearnerSkillsRegistry;
  }

  public ngOnInit(): void {
    this.authUser = this.authService.authUser;
    this.disableLearnerSkillsRegistry =
      this.authUser?.disableLearnerSkillsRegistry !== undefined
        ? this.authUser.disableLearnerSkillsRegistry
        : true;

    this.initTranslations();
    this.initialized = true;

    if (this.showSuggestions) {
      this.ngbDropdownRef.open();
    }
  }

  public ngAfterViewInit(): void {
    // sets up the observable that tracks user input in the search field
    this.initializeSearchSubscriber();

    this.suggestions = [];
    this.getSuggestions(true).subscribe((suggestions) => {
      this.showSuggestions = true;
      this.processSuggestions(suggestions);
    });
  }

  public ngOnDestroy(): void {
    if (this.inputKeyUp$) {
      this.inputKeyUp$.unsubscribe();
    }
  }

  public onSearchFocus(): void {
    if (!this.newItem) {
      this.showSuggestions = true;
    }
  }

  public add($event: Event, item: ResourceResult): void {
    $event?.preventDefault();

    this.term = null;
    this.newItem = null;
    this.showSuggestions = false;

    // don't add if already exists
    for (const selectedResult of this.selectedSearchResults) {
      if (
        selectedResult.referenceType === item.referenceType &&
        selectedResult.referenceId === item.referenceId
      ) {
        return;
      }
    }

    this.selectedSearchResults.splice(0, 0, item);
    this.hasOrgManagedContent = this.checkForOrgManagedContent();
    this.getSuggestions(false).subscribe((suggestions) => {
      this.processSuggestions(suggestions, false);
    });
  }

  public remove(index: number): void {
    this.selectedSearchResults.splice(index, 1);
    this.hasOrgManagedContent = this.checkForOrgManagedContent();
  }

  public suggestionKeydown($event, item: ResourceResult) {
    if ($event.key === Key.Enter) {
      this.add($event, item);
    }
  }

  public createAPlan() {
    const showRole: boolean = !!this.authUser?.canManageTargets;

    this.targetResourceModalService
      .createAPlan(this.target, showRole)
      .subscribe((newTarget) => {
        this.selectedSearchResults = [newTarget, ...this.selectedSearchResults];
        this.showSuggestions = false;

        this.cdr.detectChanges();
      });
  }

  public save() {
    if (this.selectedSearchResults.length === 0) {
      return;
    }

    this.saving = true;

    if (
      this.newItem &&
      !(this.newItem as ResourceResult)?.referenceId &&
      this.allowCustomResources
    ) {
      this.addCustomResource();
    }

    const newItems = this.selectedSearchResults.map(
      (result: ResourceResult) => {
        return {
          referenceId: result.referenceId as number,
          referenceType: result.referenceType,
          resourceType: result.referenceType,
          isEndorsed: result.isEndorsed,
        };
      }
    );
    // can just add the items as resources
    return this.addTargetResources(newItems, this.selectedSearchResults);
  }

  private checkForOrgManagedContent() {
    if (!this.orgManagedNotificationFlag) {
      return false;
    }
    return (
      this.selectedSearchResults.filter((item) => {
        return item?.organizationId;
      }).length > 0
    );
  }

  private initializeSearchSubscriber() {
    this.ngzone.runOutsideAngular(() => {
      this.inputKeyUp$ = fromEvent(this.inputElRef.nativeElement, 'keyup')
        .pipe(
          debounceTime(500),
          // requires at least two characters
          filter(
            (keyboardEvent: any) => keyboardEvent.target.value.length >= 2
          ),
          // filter out a11y keys
          filter(
            (e: any) =>
              e.which != Key.Left && e.which != Key.Right && e.which != Key.Tab
          ),
          // filter out if search field hasn't change
          filter(
            (keyboardEvent: any) => keyboardEvent.target.value !== this.newItem
          ),
          switchMap((keyboardEvent: any) => {
            this.newItem = keyboardEvent.target.value;
            this.noResults = false;
            this.cdr.detectChanges();
            return this.getSuggestions(false, keyboardEvent.target.value);
          })
        )
        .subscribe((suggestions) => {
          this.processSuggestions(suggestions);
        });
    });
  }

  private addCustomResource() {
    const customResource: Partial<ResourceResult> = {
      referenceType: this.resourceSection.resourceType,
      referenceId: this.newItem as string,
      title: this.newItem as string,
    };
    this.add(null, customResource as any);
  }

  private addTargetResources(newItems, closeResult = null) {
    if (this.customSaveResources) {
      this.customSaveResources(newItems);
    } else {
      return this.targetService
        .addTargetResources(
          this.resourceSection.targetId,
          this.target?.name,
          this.target?.targetType,
          this.resourceSection.parentNode,
          newItems as any
        )
        .subscribe(
          () => {
            this.activeModal.close(closeResult || newItems);
          },
          () => {
            this.saving = false;
          }
        );
    }
  }

  private getSuggestions(
    init: boolean,
    searchTerm?: string
  ): Observable<ResourceResult[]> {
    const existingReferences: ResourceReference[] = this.existingResources.map(
      (resource) => {
        return {
          referenceTitle: resource.reference.title,
          referenceType: resource.referenceType,
          referenceId: resource.referenceId,
          endorsedSrc: resource.reference.isEndorsed ? this.endorsedSrc : '',
          isEndorsed: resource.reference.isEndorsed,
        } as ResourceReference;
      }
    );

    const selectedReferences: ResourceReference[] =
      this.selectedSearchResults.map((result) => {
        return {
          referenceTitle: result.title,
          referenceType: result.referenceType,
          referenceId: result.referenceId,
          endorsedSrc: result.isEndorsed ? this.endorsedSrc : '',
          isEndorsed: result.isEndorsed,
        } as ResourceReference;
      });

    const targetInfo = this.targetService.targetResponse;
    const parentTarget = targetInfo?.target;
    const orgId = this.target?.organizationId || parentTarget?.organizationId;

    const searchParams = {
      TargetId: this.resourceSection.targetId || -1,
      ResourceSection: this.resourceSection.resourceType,
      OrganizationId: orgId || this.authUser.defaultOrgId,
      Context: this.target?.title || parentTarget?.title || '',
      Term: searchTerm || '',
      ExclusionList: existingReferences.concat(selectedReferences),
      Skip: 0,
      Take: 20,
    };

    return this.targetResourceModalService
      .getSuggestions(
        searchParams,
        this.endorsedSrc,
        init,
        this.target,
        searchTerm
      )
      .pipe(catchError(() => of([] as ResourceResult[])));
  }

  private processSuggestions(suggestions, showSuggestions: boolean = true) {
    if (suggestions.length === 0) {
      // present the special 'add new' custom item if no matching results returned
      this.noResults = true;
    }

    this.suggestions = suggestions.map((s) => {
      s.endorsedSrc = s.isEndorsed ? this.endorsedSrc : '';
      return s;
    });
    this.cdr.detectChanges();
  }

  private initTranslations() {
    const t = this.targetResourceModalService.initTranslations(
      this.resourceSection.resourceType
    );
    this.i18n.TargetResourcesForm_Title = t.targetResourcesForm_Title;
    this.i18n.TargetResourcesForm_SearchPlaceHolder =
      t.targetResourcesForm_SearchPlaceHolder;
  }
}
